import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import MaterialIssueNote from '../../components/IssueNoteHeader';
import ItemLotScanner from '../../components/ItemLotScanner';
import { apiFetch, downloadPdf } from '../../api'; // Adjust path as needed

export default function ReturnNote() {
  const { id } = useParams();
  const isViewMode = Boolean(id);

  const [issueNoteData, setIssueNoteData] = useState(null);
  const [scannedItems, setScannedItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(isViewMode);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  /* =========================
     FETCH DATA FOR VIEW MODE
     ========================= */
  useEffect(() => {
    if (!isViewMode) return;

    const fetchMaterialIssue = async () => {
      try {
        const res = await apiFetch(`/api/material-return/view?id=${id}`);

        if (!res?.success) {
          setSaveStatus({ type: 'error', message: res?.message || 'Failed to load material return note' });
          return;
        }

        // Populate the form with fetched data
        setIssueNoteData(res.data.issue_note);
        setScannedItems((res.data.items || []).map(item => ({
          ...item,
          // Ensure all required fields are present
          item_description: item.item_description || '',
          uom: item.uom || '',
          weight: item.weight || 0,
          qty: item.qty || 0,
          total_weight: item.total_weight || 0,
          lot_code: item.lot_code || '',
          scan_sequence: item.scan_sequence || 0,
        })));

      } catch (err) {
        console.error('Failed to load material return note', err);
        setSaveStatus({ type: 'error', message: 'Failed to load material return note' });
      } finally {
        setLoading(false);
      }
    };

    fetchMaterialIssue();
  }, [id, isViewMode]);

  const handleIssueNoteUpdate = useCallback((data) => {
    setIssueNoteData(data);
  }, []);

  const handleItemsUpdate = useCallback((items) => {
    setScannedItems(items);
  }, []);

  const validateData = () => {
    if (!issueNoteData) {
      return { valid: false, message: 'Material Return Note data is missing' };
    }

    if (!issueNoteData.min_no && !issueNoteData.autoGenerate) {
      return { valid: false, message: 'MRN No is required or enable auto-generation' };
    }

    if (!issueNoteData.date_time) {
      return { valid: false, message: 'Date/Time is required' };
    }

    if (!issueNoteData.person_issuing) {
      return { valid: false, message: 'Person Returning is required' };
    }

    if (scannedItems.length === 0) {
      return { valid: false, message: 'No items scanned. Please scan at least one item.' };
    }

    return { valid: true, message: '' };
  };

  const handleSave = async () => {
    // Validate data
    const validation = validateData();
    if (!validation.valid) {
      setSaveStatus({ type: 'error', message: validation.message });
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 5000);
      return;
    }

    setIsSaving(true);
    setSaveStatus({ type: 'info', message: 'Saving data...' });

    try {
      // Combine data from both components
      const combinedData = {
        issue_note: issueNoteData,
        items: scannedItems,
        metadata: {
          total_items: scannedItems.length,
          total_qty: scannedItems.reduce((sum, item) => sum + item.qty, 0),
          total_weight: scannedItems.reduce((sum, item) => sum + item.total_weight, 0),
          created_at: new Date().toISOString(),
        }
      };

      // If MRN No is empty, generate one
      if (!combinedData.issue_note.min_no) {
        combinedData.issue_note.min_no = `MRN-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
        combinedData.issue_note.auto_generated = true;
      }

      // Save to API
      const response = await apiFetch('/api/material-return/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(combinedData),
      });

      if (response.success) {
        setSaveStatus({ 
          type: 'success', 
          message: `Material Return Note saved successfully! MRN No: ${combinedData.issue_note.min_no}` 
        });
        
        // Reset form after successful save if needed
        setTimeout(() => {
          setSaveStatus({ type: '', message: '' });
        }, 3000);
      } else {
        throw new Error(response.message || 'Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus({ 
        type: 'error', 
        message: `Save failed: ${error.message}` 
      });
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all data? This action cannot be undone.')) {
      // Reset both components by triggering their internal reset
      setIssueNoteData(null);
      setScannedItems([]);
      setSaveStatus({ type: '', message: '' });
      
      // You might want to pass reset callbacks to components
      // or use refs to call their internal reset functions
    }
  };

  /* =========================
     DOWNLOAD PDF
     ========================= */
  const handleDownloadPdf = async () => {
    if (!id || !issueNoteData?.min_no) {
      alert('Cannot download PDF: Material Return Note data not available');
      return;
    }

    try {
      setDownloadingPdf(true);
      
      const blob = await downloadPdf(`/pdf-return.php?id=${id}`);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Material_Return_Note_${issueNoteData.min_no}.pdf`;
      
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('PDF download failed:', error);
      alert(`PDF download failed: ${error.message}`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  /* =========================
     LOADING STATE
     ========================= */
  if (loading) {
    return (
      <Layout>
        <h2>Loading material return note...</h2>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        {/* Page Title */}
        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.pageTitle}>
              {isViewMode ? 'View Material Return Note' : 'Create Material Return Note'}
            </h1>
            {isViewMode && issueNoteData?.min_no && (
              <p style={styles.pageSubtitle}>MRN No: {issueNoteData.min_no}</p>
            )}
          </div>
          
          {/* PDF Download Button - only show in view mode */}
          {isViewMode && issueNoteData?.min_no && (
            <button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              style={{
                padding: '10px 20px',
                backgroundColor: downloadingPdf ? '#93c5fd' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: downloadingPdf ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: downloadingPdf ? 0.8 : 1,
              }}
            >
              {downloadingPdf ? (
                <>
                  <span style={styles.spinner}></span>
                  Downloading PDF...
                </>
              ) : (
                <>
                  📄 Download PDF
                </>
              )}
            </button>
          )}
        </div>

        {/* Save Status Banner */}
        {saveStatus.message && (
          <div style={styles.saveStatus(saveStatus.type)}>
            <div style={styles.statusIcon}>
              {saveStatus.type === 'success' ? '✓' : 
               saveStatus.type === 'error' ? '✗' : '⏳'}
            </div>
            <div style={styles.statusMessage}>{saveStatus.message}</div>
            <button 
              style={styles.statusClose}
              onClick={() => setSaveStatus({ type: '', message: '' })}
            >
              ×
            </button>
          </div>
        )}

        <MaterialIssueNote onDataUpdate={handleIssueNoteUpdate} initialData={issueNoteData} title="Material Return Note" codePrefix="MRN" />
        <ItemLotScanner onItemsUpdate={handleItemsUpdate} initialItems={scannedItems} isViewMode={isViewMode}/>
        
        {/* Combined Save Button */}
        <div style={styles.saveSection}>
          <div style={styles.summaryCard}>
            <h3 style={styles.summaryTitle}>Summary</h3>
            <div style={styles.summaryGrid}>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>MRN Status:</div>
                <div style={styles.summaryValue}>
                  {issueNoteData?.min_no ? 'Ready' : 'Pending'}
                </div>
              </div>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Items Scanned:</div>
                <div style={styles.summaryValue}>{scannedItems.length}</div>
              </div>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Total Quantity:</div>
                <div style={styles.summaryValue}>
                  {scannedItems.reduce((sum, item) => sum + item.qty, 0)}
                </div>
              </div>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Total Weight:</div>
                <div style={styles.summaryValue}>
                  {scannedItems.reduce((sum, item) => sum + item.total_weight, 0).toFixed(2)} kg
                </div>
              </div>
            </div>
          </div>

          <div style={styles.actionButtons}>
            <button 
              style={styles.resetButton}
              onClick={handleReset}
              disabled={isSaving}
            >
              Reset All
            </button>
            <button 
              style={styles.saveButton(isSaving)}
              onClick={handleSave}
              disabled={isSaving || scannedItems.length === 0}
            >
              {isSaving ? (
                <>
                  <span style={styles.spinner}></span>
                  {isViewMode ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                isViewMode ? 'Update Material Return Note' : 'Save Material Return Note'
              )}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* =========================
   STYLES
   ========================= */
const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '16px',
    width: '100%',
  },
  
  saveStatus: (type) => ({
    marginBottom: '16px',
    padding: '12px 16px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: type === 'success' ? '#d1fae5' : 
                   type === 'error' ? '#fee2e2' : '#fef3c7',
    border: `1px solid ${type === 'success' ? '#a7f3d0' : 
                       type === 'error' ? '#fecaca' : '#fde68a'}`,
    color: type === 'success' ? '#065f46' : 
           type === 'error' ? '#991b1b' : '#92400e',
  }),
  
  statusIcon: {
    fontSize: '18px',
    fontWeight: 'bold',
  },
  
  statusMessage: {
    flex: 1,
    fontSize: '14px',
    fontWeight: '500',
  },
  
  statusClose: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: 'inherit',
    padding: '0',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  saveSection: {
    background: 'white',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #e5e7eb',
  },
  
  summaryCard: {
    marginBottom: '20px',
    paddingBottom: '20px',
    borderBottom: '1px solid #e5e7eb',
  },
  
  summaryTitle: {
    margin: '0 0 16px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
  },
  
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
  },
  
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  
  summaryLabel: {
    fontSize: '13px',
    color: '#6b7280',
    fontWeight: '500',
  },
  
  summaryValue: {
    fontSize: '16px',
    color: '#1f2937',
    fontWeight: '600',
  },
  
  actionButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
  },
  
  saveButton: (isLoading) => ({
    padding: '12px 32px',
    backgroundColor: isLoading ? '#93c5fd' : '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: isLoading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '200px',
    height: '44px',
    justifyContent: 'center',
    opacity: isLoading ? 0.8 : 1,
    ':hover': isLoading ? {} : {
      backgroundColor: '#2563eb',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 6px rgba(37, 99, 235, 0.2)',
    },
  }),
  
  resetButton: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '120px',
    height: '44px',
    ':hover': {
      backgroundColor: '#f3f4f6',
      color: '#374151',
    },
  },
  
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  
  pageHeader: {
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
  },
  
  pageTitle: {
    margin: '0 0 8px 0',
    fontSize: '24px',
    fontWeight: '600',
    color: '#1f2937',
  },
  
  pageSubtitle: {
    margin: '0',
    fontSize: '16px',
    color: '#6b7280',
  },
};